import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Plus, X, Sparkles, Presentation, Loader2, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

const LAYOUT_COLOR = {
  title: "#D4AF37",
  content: "#94A3B8",
  two_column: "#3B82F6",
  stat: "#22C55E",
  quote: "#A855F7",
};

export default function Studio() {
  const [decks, setDecks] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ slide_count: 8, audience: "executive" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get("/studio/decks");
    setDecks(r.data);
    if (!active && r.data[0]) setActive(r.data[0]);
  };
  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    if (!form.title || !form.objective) { toast.error("Title and objective required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/studio/generate", form);
      setActive(r.data); refresh();
      setOpen(false); setForm({ slide_count: 8, audience: "executive" });
      toast.success(`${r.data.slides?.length || 0}-slide deck generated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete deck?")) return;
    await api.delete(`/studio/decks/${id}`);
    setActive(null); refresh();
  };

  const exportPptx = async () => {
    try {
      const r = await api.get(`/studio/decks/${active.id}/export?format=pptx`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${active.title.replace(/\s/g, "_")}.pptx`;
      a.click();
      toast.success("PPTX exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="studio-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">// Design & Presentation Studio</div>
          <h1 className="font-display text-4xl font-medium text-white tracking-tight">Presentation Studio</h1>
          <p className="text-slate-400 mt-2 text-sm">AI-generated investor decks and executive presentation outlines.</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-deck-btn"
          className="px-4 py-2 flex items-center gap-2 font-medium text-sm"
          style={{ background: "var(--gold-500)", color: "#050B14" }}
        >
          <Sparkles className="w-4 h-4" /> Generate Deck
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-3 space-y-2">
          <div className="label-eyebrow mb-2">// Decks ({decks.length})</div>
          {decks.map((d) => (
            <motion.div
              layout key={d.id}
              onClick={() => setActive(d)}
              data-testid={`deck-${d.id}`}
              className={`p-4 cursor-pointer border ${active?.id === d.id ? "border-[#D4AF37]/50 bg-[#10182B]" : "border-white/10 hover:border-[#D4AF37]/30 bg-[#0A111F]"}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Presentation className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.5} />
                <span className="label-eyebrow">{d.audience}</span>
              </div>
              <div className="font-display text-sm text-white leading-tight">{d.title}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-1.5">{d.slides?.length || 0} slides</div>
            </motion.div>
          ))}
          {!decks.length && <div className="text-sm text-slate-500 p-6 border border-white/5">No decks yet.</div>}
        </div>

        <div className="col-span-12 lg:col-span-9">
          {active ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-eyebrow mb-1">// {active.audience} · {active.slides?.length || 0} slides</div>
                  <h2 className="font-display text-2xl text-white">{active.title}</h2>
                  <div className="text-sm text-slate-400 mt-1 max-w-2xl">{active.objective}</div>
                </div>
                <button onClick={() => remove(active.id)} className="p-2 border border-white/10 text-slate-500 hover:text-red-400 hover:border-red-500/50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="flex justify-end -mt-2">
                <button
                  onClick={exportPptx}
                  data-testid="export-pptx-btn"
                  className="px-4 py-2 flex items-center gap-2 text-sm font-medium border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050B14] transition-all"
                >
                  <Download className="w-4 h-4" /> Export PowerPoint (.pptx)
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(active.slides || []).map((s, i) => (
                  <div key={i} className="nexus-card p-5 aspect-[16/10] flex flex-col" data-testid={`slide-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-[10px] text-slate-500 tracking-widest">SLIDE {String(i + 1).padStart(2, "0")}</span>
                      <span className="font-mono text-[10px] tracking-widest uppercase" style={{ color: LAYOUT_COLOR[s.layout] || "#94A3B8" }}>
                        {s.layout}
                      </span>
                    </div>
                    <div className="font-display text-lg text-white mb-3 leading-tight">{s.title}</div>
                    <ul className="space-y-1.5 flex-1">
                      {(s.bullets || []).map((b, j) => (
                        <li key={j} className="text-xs text-slate-300 flex gap-2 leading-relaxed">
                          <span className="text-[#D4AF37] mt-0.5 shrink-0">▸</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {s.notes && (
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <div className="label-eyebrow mb-1">// notes</div>
                        <div className="text-[11px] text-slate-500 italic leading-relaxed">{s.notes}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="nexus-card p-10 text-center text-slate-500 text-sm">
              <Presentation className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1} />
              No deck selected. Generate your first one.
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A111F] border border-white/10 p-8 w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="label-eyebrow mb-1">// AI Deck Engine</div>
                <h2 className="font-display text-2xl text-white">Generate Executive Deck</h2>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Deck title *">
                <input data-testid="deck-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="dk-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Audience">
                  <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="dk-input">
                    <option value="executive">Executive</option>
                    <option value="investor">Investor</option>
                    <option value="government">Government</option>
                    <option value="board">Board</option>
                    <option value="client">Client</option>
                  </select>
                </Field>
                <Field label="Slide count">
                  <input type="number" min={4} max={20} value={form.slide_count} onChange={(e) => setForm({ ...form, slide_count: +e.target.value })} className="dk-input" />
                </Field>
              </div>
              <Field label="Objective *">
                <textarea rows={3} data-testid="deck-objective" value={form.objective || ""} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="dk-input resize-none" placeholder="What does this deck need to achieve?" />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-white/10 text-slate-300">Cancel</button>
              <button
                onClick={generate}
                disabled={busy}
                data-testid="gen-deck-btn"
                className="px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-60"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Crafting…" : "Generate"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <style>{`.dk-input { width:100%; padding:0.5rem 0.75rem; background:#050B14; border:1px solid rgba(255,255,255,0.1); color:#F8FAFC; font-family:'IBM Plex Mono', monospace; font-size:0.875rem; outline:none; } .dk-input:focus { border-color:#D4AF37; }`}</style>
    </div>
  );
}
const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
